import { Dialog } from '../components/Dialog';
import { Icon } from '../components/Icon';
import { useI18n } from '../i18n';
import { AddMomentDialog } from '../features/photos/AddMomentDialog';
import { useApp } from './AppState';
import { useNavigate } from './router';

/**
 * The Add tab: what you share with the family — a photo, a family milestone (a
 * simcha), an everyday event, a recorded story, or a recipe. Opens as a sheet
 * over the current screen; the photo and event forms then open in their own
 * dialog, a story goes to the recording screen, and a recipe opens its editor
 * over wherever you are.
 *
 * Adding a *person* is deliberately not here: the tree grows from one place
 * only, the burger menu (see app/Shell.tsx → BurgerMenu).
 */
export function AddSheet() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { adding, openAdd, closeAdd, openRecipeEditor, session } = useApp();

  const choices = [
    { kind: 'photo' as const, icon: 'camera', title: t('add.photo'), help: t('add.photoHelp'), accent: 'primary' },
    { kind: 'milestone' as const, icon: 'milestone', title: t('add.milestone'), help: t('add.milestoneHelp'), accent: 'milestone' },
    { kind: 'event' as const, icon: 'sparkle', title: t('add.event'), help: t('add.eventHelp'), accent: 'memory' },
    { kind: 'story' as const, icon: 'mic', title: t('add.story'), help: t('add.storyHelp'), accent: 'memory' },
    // Last, and in its own colour: the newest thing here, and the only one that does not land on the timeline.
    { kind: 'recipe' as const, icon: 'recipe', title: t('add.recipe'), help: t('add.recipeHelp'), accent: 'recipe' },
  ];

  const choose = (kind: (typeof choices)[number]['kind']) => {
    if (kind === 'story') {
      closeAdd();
      navigate('/record');
    } else if (kind === 'recipe') {
      // Your own, to start with — the editor asks whose it is, and lets that be changed, for the grandchild writing down Savta's.
      closeAdd();
      openRecipeEditor();
    } else openAdd(kind);
  };

  return (
    <>
      <Dialog open={adding === 'menu'} onClose={closeAdd} title={t('add.sheetTitle')} labelledBy="add-sheet-title">
        {/* The choices are the explanation; a sentence above them saying the same thing was a line of reading before the first tap. */}
        <div className="add-choices">
          {choices.map((c) => (
            <button key={c.kind} type="button" className={`add-choice is-${c.accent}`} data-assist={`add.${c.kind}`} onClick={() => choose(c.kind)} disabled={!session}>
              <span className="add-choice-icon" aria-hidden="true"><Icon name={c.icon} /></span>
              <span className="add-choice-text">
                <span className="add-choice-title">{c.title}</span>
                <span className="add-choice-help">{c.help}</span>
              </span>
              <Icon name="forward" className="icon-mirror add-choice-arrow" />
            </button>
          ))}
        </div>
      </Dialog>
      <AddMomentDialog kind={adding === 'photo' || adding === 'event' || adding === 'milestone' ? adding : null} onClose={closeAdd} />
    </>
  );
}
